import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { vehicleAPI } from '../../api/index';
import { queryClient } from '../../App';
import { FuelType, Vehicle, VehicleType } from '../../types/index';
import { Button } from '../Button';

interface AddVehicleModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  vehicle?: Vehicle
  onSuccess?: () => void
}

const vehicleTypes: VehicleType[] = [
  'two_wheeler',
  'three_wheeler',
  'four_wheeler',
  'commercial',
  'other',
]

const fuelTypes: FuelType[] = [
  'petrol',
  'diesel',
  'electric',
  'cng',
  'hybrid',
  'lpg',
]

export default function AddVehicleModal({
  open,
  onClose,
  customerId,
  vehicle,
  onSuccess,
}: AddVehicleModalProps) {
  const [formData, setFormData] = useState({
    registration_number: '',
    make: '',
    model: '',
    variant: '',
    color: '',
    year: '',
    vehicle_type: '' as VehicleType | '',
    fuel_type: '' as FuelType | '',
    chassis_number: '',
    engine_number: '',
    seating_capacity: '',
    registration_date: '',
    rto_office: '',
    hypothecation: '',
  })

  useEffect(() => {
    if (vehicle) {
      setFormData({
        registration_number: vehicle.registration_number || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        variant: vehicle.variant || '',
        color: vehicle.color || '',
        year: vehicle.year ? String(vehicle.year) : '',
        vehicle_type: vehicle.vehicle_type || '',
        fuel_type: vehicle.fuel_type || '',
        chassis_number: vehicle.chassis_number || '',
        engine_number: vehicle.engine_number || '',
        seating_capacity: vehicle.seating_capacity ? String(vehicle.seating_capacity) : '',
        registration_date: vehicle.registration_date || '',
        rto_office: vehicle.rto_office || '',
        hypothecation: vehicle.hypothecation || '',
      })
    } else {
      setFormData({
        registration_number: '',
        make: '',
        model: '',
        variant: '',
        color: '',
        year: '',
        vehicle_type: '',
        fuel_type: '',
        chassis_number: '',
        engine_number: '',
        seating_capacity: '',
        registration_date: '',
        rto_office: '',
        hypothecation: '',
      })
    }
  }, [vehicle, open])

  const buildPayload = () => ({
    registration_number: formData.registration_number.trim(),
    make: formData.make.trim() || null,
    model: formData.model.trim() || null,
    variant: formData.variant.trim() || null,
    color: formData.color.trim() || null,
    year: formData.year ? parseInt(formData.year, 10) : null,
    vehicle_type: formData.vehicle_type || null,
    fuel_type: formData.fuel_type || null,
    chassis_number: formData.chassis_number.trim() || null,
    engine_number: formData.engine_number.trim() || null,
    seating_capacity: formData.seating_capacity
      ? parseInt(formData.seating_capacity, 10)
      : null,
    registration_date: formData.registration_date || null,
    rto_office: formData.rto_office.trim() || null,
    hypothecation: formData.hypothecation.trim() || null,
  })

  const createMutation = useMutation({
    mutationFn: () => vehicleAPI.create(customerId, buildPayload() as any),
    onSuccess: () => {
      toast.success('Vehicle created')
      queryClient.invalidateQueries({ queryKey: ['vehicles', customerId] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to create vehicle')
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => vehicleAPI.update(vehicle!.id, buildPayload() as any),
    onSuccess: () => {
      toast.success('Vehicle updated')
      queryClient.invalidateQueries({ queryKey: ['vehicles', customerId] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle!.id] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update vehicle')
    },
  })

  const isLoading = createMutation.isPending || updateMutation.isPending
  const isEdit = !!vehicle

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.registration_number.trim()) {
      toast.error('Registration number is required')
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Vehicle' : 'Add Vehicle'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Registration Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.registration_number}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    registration_number: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="MH 01 AB 1234"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Make
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.make}
                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Maruti"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Model
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Swift"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Variant
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.variant}
                onChange={(e) =>
                  setFormData({ ...formData, variant: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="VXi AMT"
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Color
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Silver"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Year
              </label>
              <input
                type="number"
                disabled={isLoading}
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="2023"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Seating Capacity
              </label>
              <input
                type="number"
                disabled={isLoading}
                value={formData.seating_capacity}
                onChange={(e) =>
                  setFormData({ ...formData, seating_capacity: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="5"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle Type
              </label>
              <select
                disabled={isLoading}
                value={formData.vehicle_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vehicle_type: e.target.value as VehicleType | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select vehicle type</option>
                {vehicleTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fuel Type
              </label>
              <select
                disabled={isLoading}
                value={formData.fuel_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    fuel_type: e.target.value as FuelType | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select fuel type</option>
                {fuelTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 5 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Chassis Number
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.chassis_number}
                onChange={(e) =>
                  setFormData({ ...formData, chassis_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="CHAS123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Engine Number
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.engine_number}
                onChange={(e) =>
                  setFormData({ ...formData, engine_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="ENG123456789"
              />
            </div>
          </div>

          {/* Row 6 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Registration Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.registration_date}
                onChange={(e) =>
                  setFormData({ ...formData, registration_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                RTO Office
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.rto_office}
                onChange={(e) =>
                  setFormData({ ...formData, rto_office: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Mumbai RTO"
              />
            </div>
          </div>

          {/* Row 7 */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Hypothecation
            </label>
            <input
              type="text"
              disabled={isLoading}
              value={formData.hypothecation}
              onChange={(e) =>
                setFormData({ ...formData, hypothecation: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              placeholder="Bank Name / None"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update Vehicle' : 'Add Vehicle'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
